import { useCallback, useEffect, useRef, useMemo } from 'react';

import { useAnalyticsClient } from '@wac-web/analytics/wac-analytics/analytics-client';

import { AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';

type Attributes = {
	[key: string]: boolean | string | number | undefined | null | Attributes;
};

const EMPTY_ATTRIBUTES: Attributes = {};

export const useFireAnalyticsEvents = (initialAttributes?: Attributes | null) => {
	const analyticsClient = useAnalyticsClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const pendingEvents = useRef<AnalyticsEventPayload[]>([]);

	// Memoize initialAttributes to prevent unnecessary re-renders
	const memoizedInitialAttributes = useMemo(
		() => initialAttributes ?? EMPTY_ATTRIBUTES,
		[initialAttributes],
	);

	const fireEvent = useCallback(
		(eventPayload: AnalyticsEventPayload) => {
			if (analyticsClient) {
				createAnalyticsEvent({
					...eventPayload,
					attributes: {
						...memoizedInitialAttributes,
						...(eventPayload['attributes'] || {}),
					},
				}).fire();
			} else {
				// store events to send later
				pendingEvents.current.push(eventPayload);
			}
		},
		[analyticsClient, createAnalyticsEvent, memoizedInitialAttributes],
	);

	// fires pending events once client is available
	useEffect(() => {
		if (analyticsClient && pendingEvents.current.length > 0) {
			pendingEvents.current.forEach((eventPayload) => {
				createAnalyticsEvent({
					...eventPayload,
					attributes: {
						...memoizedInitialAttributes,
						...(eventPayload['attributes'] || {}),
					},
				}).fire();
			});
			pendingEvents.current = [];
		}
		// we only need to run this effect when the analytics client changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analyticsClient]);

	const fireUIEvent = useCallback(
		(eventPayload: AnalyticsEventPayload) => {
			fireEvent({
				...eventPayload,
				eventType: 'ui',
			});
		},
		[fireEvent],
	);

	const fireTrackEvent = useCallback(
		(eventPayload: AnalyticsEventPayload) => {
			fireEvent({
				...eventPayload,
				eventType: 'track',
			});
		},
		[fireEvent],
	);

	const fireScreenEvent = useCallback(
		(eventPayload: AnalyticsEventPayload) => {
			fireEvent({
				...eventPayload,
				eventType: 'screen',
			});
		},
		[fireEvent],
	);

	const fireOperationalEvent = useCallback(
		(eventPayload: AnalyticsEventPayload) => {
			fireEvent({
				...eventPayload,
				eventType: 'operational',
			});
		},
		[fireEvent],
	);

	return {
		fireUIEvent,
		fireTrackEvent,
		fireScreenEvent,
		fireOperationalEvent,
		pendingEvents,
	};
};
